import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton, useToast } from "@chotca/ui";
import type { Gender, AdminUpdatePostingRequest } from "@chotca/config";
import { getSdk, qk } from "@chotca/api-client";
import {
  usePostingEditConstraints,
  useArchiveOrgAddress,
  useCreateOrgAddress,
  useOrgAddresses,
  useUpdateOrgAddress,
  useUploadAdminPostingImage,
} from "@chotca/api-client";
import {
  EditPostingForm,
  PostingNotEditable,
  postingToEditValues,
  type PostingFormOutput,
} from "@chotca/posting-form";
import { AddressPicker } from "@/components/address-picker";

export function EditPostingClient({ postingId }: { postingId: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const postingQ = useQuery({
    // Share the admin endpoint with the detail page — they use the same query
    // key, so reading the public getById here (which omits the admin-only
    // sourceUrl) would pollute the cache and make the FB source link vanish
    // after an edit. admin.getPosting returns the same shape with sourceUrl.
    queryKey: qk.admin.posting(postingId),
    queryFn: () => getSdk().admin.getPosting(postingId),
  });
  const constraintsQ = usePostingEditConstraints(postingId);
  const p = postingQ.data;
  const orgId = p?.orgId ?? "";

  const addressesQ = useOrgAddresses(orgId || null);
  const createAddress = useCreateOrgAddress(orgId);
  const updateAddress = useUpdateOrgAddress(orgId);
  const archiveAddress = useArchiveOrgAddress(orgId);
  const uploadPreview = useUploadAdminPostingImage(orgId);

  const update = useMutation({
    mutationFn: (body: AdminUpdatePostingRequest) =>
      getSdk().admin.updatePosting(postingId, body),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã lưu thay đổi" });
      qc.invalidateQueries({ queryKey: qk.admin.posting(postingId) });
      qc.invalidateQueries({
        queryKey: qk.postings.editConstraints(postingId),
      });
      nav(`/postings/${postingId}`);
    },
  });

  if (postingQ.isLoading || constraintsQ.isLoading)
    return <Skeleton className="h-96 rounded-2xl" />;
  if (!p)
    return (
      <p className="text-center text-sm text-ash">Không tìm thấy tin này.</p>
    );

  const backHref = `/postings/${postingId}`;

  if (!constraintsQ.data?.editable) {
    return (
      <div className="space-y-6">
        <Link to={backHref} className="text-xs text-ash hover:text-ink">
          ← {p.title}
        </Link>
        <PostingNotEditable
          state={constraintsQ.data?.state ?? p.state}
          onBack={() => nav(backHref)}
        />
      </div>
    );
  }

  async function handleSubmit(values: PostingFormOutput) {
    const previewImageKey =
      values.previewImageKey != null
        ? values.previewImageKey
        : values.previewImageUrl == null
          ? null
          : undefined;
    await update.mutateAsync({
      title: values.title,
      description: values.description,
      venueType: values.venueType,
      salaryMin: values.salaryMin,
      salaryMax: values.salaryMax,
      salaryType: values.salaryType,
      headcount: values.headcount,
      benefits: values.benefits,
      skillsRequired: values.skillsRequired,
      skillsPreferred: values.skillsPreferred,
      requirements: {
        gender: (values.reqGender ?? null) as Gender | null,
        minAge: values.reqMinAge ?? null,
        maxAge: values.reqMaxAge ?? null,
        minHeightCm: values.reqMinHeightCm ?? null,
        motorbikeRequired: values.reqMotorbike,
        excludeVisibleTattoo: values.reqExcludeTattoo,
        kycCccdRequired: values.reqKyc,
      },
      softNotes: values.softNotes || null,
      ...(previewImageKey !== undefined ? { previewImageKey } : {}),
      sourceUrl: values.sourceUrl ?? null,
      addressIds: values.addressIds,
    });
  }

  return (
    <div className="space-y-4">
      <Link to={backHref} className="text-xs text-ash hover:text-ink">
        ← {p.title}
      </Link>
      <EditPostingForm
        loading={update.isPending}
        onSubmit={handleSubmit}
        initialValues={postingToEditValues(p)}
        showSourceUrl
        minHeadcount={constraintsQ.data?.committedHires}
        lockedAddressIds={constraintsQ.data?.lockedAddressIds}
        addressInterviewCounts={constraintsQ.data?.addressInterviewCounts}
        onCancel={() => nav(backHref)}
        onMobileBack={() => nav(backHref)}
        onUploadPreviewImage={async (file, onProgress) => {
          const r = await uploadPreview.mutateAsync({ file, onProgress });
          return { key: r.key, url: r.publicUrl };
        }}
        AddressPicker={AddressPicker}
        addressBundle={{
          addresses: addressesQ.data?.items ?? [],
          loading: addressesQ.isLoading,
          onCreate: (input) => createAddress.mutateAsync(input),
          onUpdate: (addrId, input) =>
            updateAddress.mutateAsync({ id: addrId, body: input }),
          onArchive: async (addrId) => {
            await archiveAddress.mutateAsync(addrId);
          },
        }}
      />
    </div>
  );
}
